/**
 * SignalR client wrapper.
 *
 * The Hub Dashboard (and any other page that wants live ticket updates)
 * imports the singleton `realtime` and subscribes to events. We keep a
 * single connection per browser tab to stay well under the SignalR
 * Service Free tier's 20-concurrent-connection cap.
 *
 * Events emitted:
 *   - 'ticket-changed' (payload: { autoTaskId, ticketNumber, status, ...})
 *     fired by the api-timers Cosmos change-feed broadcaster
 *
 * On any 'ticket-changed', a component can refetch the ticket list (or
 * just the one ticket) and re-render. The payload includes the most
 * relevant fields so a component can also do an optimistic in-place
 * update without a roundtrip.
 *
 * Connection lifecycle is automatic — auto-reconnect with backoff.
 */

import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
  HubConnectionState,
} from '@microsoft/signalr';

type Listener<P> = (payload: P) => void;

export interface TicketChangedPayload {
  autoTaskId: number;
  ticketNumber?: string;
  status?: string;
  statusId?: number;
  assignedResourceId?: number;
  assignedResourceName?: string;
  lastActivityDate?: string;
}

class RealtimeClient {
  private conn: HubConnection | null = null;
  private starting: Promise<void> | null = null;
  private listeners = new Map<string, Set<Listener<unknown>>>();

  /** Connect (idempotent). Safe to call repeatedly. */
  async connect(): Promise<void> {
    if (this.conn && (this.conn.state === HubConnectionState.Connected || this.conn.state === HubConnectionState.Connecting)) {
      return;
    }
    if (this.starting) return this.starting;

    this.starting = (async () => {
      const conn = new HubConnectionBuilder()
        .withUrl('/api/signalr/negotiate', {
          // SDK calls negotiate, then opens WebSocket against the URL it returns
          withCredentials: true,
        })
        .withAutomaticReconnect([0, 2_000, 5_000, 10_000, 30_000])
        .configureLogging(LogLevel.Warning)
        .build();

      // Forward all known events to our listener registry
      conn.on('ticket-changed', (p: TicketChangedPayload) => this.emit('ticket-changed', p));

      conn.onreconnected(() => {
        // Could re-subscribe to filters here if we add per-tech channels later
      });

      this.conn = conn;
      try {
        await conn.start();
      } catch (err) {
        // Don't blow up the page if SignalR is unreachable — pages still work
        // via polling / manual refresh as before.

        console.warn('[realtime] connect failed:', err);
        this.conn = null;
      }
    })();

    try {
      await this.starting;
    } finally {
      this.starting = null;
    }
  }

  /** Subscribe to an event. Returns an unsubscribe function. */
  on<P>(event: 'ticket-changed', cb: Listener<P>): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(cb as Listener<unknown>);
    return () => set?.delete(cb as Listener<unknown>);
  }

  private emit(event: string, payload: unknown): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const cb of set) {
      try { cb(payload); } catch (err) {
        console.warn('[realtime] listener threw:', err);
      }
    }
  }

  get state(): HubConnectionState {
    return this.conn?.state ?? HubConnectionState.Disconnected;
  }
}

export const realtime = new RealtimeClient();
